import React, { useRef } from 'react'
import { Box, Flex, Text } from '@common/fundamentals'
import { Button, CircularProgress, IconButton, Tooltip } from '@common/material'
import { ArrowBack, Clear } from '@mui/icons-material'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { useGetSiteBoxDocument } from '@core/react-query/features/siteBoxDocuments/hooks/useGetSiteBoxDocument'
import { PspdfkitComponentRef } from 'components/modal/pdf/PspdfkitComponent'
import CarouselNavigation from '@common/CarouselNavigation'
import { SiteBoxDocumentPreview } from '@modules/siteBox/components/SiteBoxDocumentPreview'
import {
  SiteBoxDocumentSidebar,
  SiteBoxDocumentSidebarRef,
} from './SiteBoxDocumentSidebar'
import { ViewType } from './types'

export interface ViewerDocumentItem {
  id: number | string
  name: string
  siteBoxDocument?: SiteBoxDocument
  documentUrl?: string
}

export interface SiteBoxFileViewActionsContext {
  editQueries: () => void
}

export interface SiteBoxFileViewProps {
  organisationId: number
  projectId: number
  documents: ViewerDocumentItem[]
  currentIndex: number
  onChangeIndex: (index: number) => void
  readOnly?: boolean
  initialView?: ViewType
  renderActions?: (
    siteBoxDocument: SiteBoxDocument,
    context: SiteBoxFileViewActionsContext,
  ) => React.ReactNode
  onBack?: () => void
  onClose?: () => void
}

const documentIntelligenceIconConfig = {
  PROCESSING: {
    icon: <CircularProgress size={20} />,
    tooltipMessage: `${localizedStrings.documentIntelligence}: ${localizedStrings.processing}`,
  },
  PARTIALLY_FOUND: {
    icon: <PriorityHighIcon color='warning' />,
    tooltipMessage: `${localizedStrings.documentIntelligence}: ${localizedStrings.issuesDetected}`,
  },
  NOT_FOUND: {
    icon: <CloseOutlinedIcon color='error' />,
    tooltipMessage: `${localizedStrings.documentIntelligence}: ${localizedStrings.issuesDetected}`,
  },
  FOUND: {
    icon: <DoneOutlinedIcon color='success' />,
    tooltipMessage: `${localizedStrings.documentIntelligence}: ${localizedStrings.noIssuesDetected}`,
  },
  NO_RESULTS: undefined,
}

export const SiteBoxFileView: React.FC<SiteBoxFileViewProps> = ({
  organisationId,
  projectId,
  documents,
  currentIndex,
  onChangeIndex,
  readOnly,
  initialView,
  renderActions,
  onBack,
  onClose,
}) => {
  const pspdfkitRef = useRef<PspdfkitComponentRef>(null)
  const sidebarRef = useRef<SiteBoxDocumentSidebarRef>(null)

  const hasMultipleDocuments = documents.length > 1
  const currentItem = documents[currentIndex] ?? documents[0]
  // Refetch so the header and actions reflect renames/archives made from inside the view.
  const { data: fetchedSiteBoxDocument } = useGetSiteBoxDocument({
    documentId: currentItem?.siteBoxDocument?.id,
  })
  const siteBoxDocument = fetchedSiteBoxDocument ?? currentItem?.siteBoxDocument
  // Preview uses the list item's URL so a re-signed URL from the refetch doesn't reload PSPDFKit.
  const previewSiteBoxDocument = currentItem?.siteBoxDocument
  const documentUrl =
    currentItem?.documentUrl ??
    previewSiteBoxDocument?.currentVersion?.documentGetUrl
  const previewMode =
    previewSiteBoxDocument?.currentVersion?.getPreviewMode?.() ?? 'PDF'
  const documentIntelligenceIcon =
    siteBoxDocument &&
    documentIntelligenceIconConfig[
      siteBoxDocument.getDocumentIntelligenceIcon()
    ]

  const handlePrevious = () =>
    onChangeIndex(currentIndex === 0 ? documents.length - 1 : currentIndex - 1)
  const handleNext = () =>
    onChangeIndex(currentIndex === documents.length - 1 ? 0 : currentIndex + 1)

  return (
    <Flex
      flexDirection='column'
      flex={1}
      height='100%'
      minHeight={0}
      bgcolor={Palette.Common.White}
    >
      <Flex
        px={2}
        py={1.5}
        alignItems='center'
        justifyContent='space-between'
        borderBottom={`1px solid ${Palette.Grey[300]}`}
      >
        <Flex alignItems='center' gap={1} minWidth={0}>
          {onBack && (
            <Button
              variant='outlined'
              size='medium'
              startIcon={<ArrowBack />}
              sx={{ '.MuiButton-startIcon': { margin: 0 }, minWidth: 0, mr: 1 }}
              onClick={onBack}
            />
          )}
          <Text fontSize={20} noWrap>
            {siteBoxDocument?.name ?? currentItem?.name ?? ''}
          </Text>
          {documentIntelligenceIcon && (
            <Tooltip title={documentIntelligenceIcon.tooltipMessage}>
              <Box component='span' display='inline-flex' alignItems='center'>
                {documentIntelligenceIcon.icon}
              </Box>
            </Tooltip>
          )}
          {hasMultipleDocuments && (
            <Text fontSize='14px' color={Palette.Grey[600]} ml={1} noWrap>
              {currentIndex + 1} of {documents.length}
            </Text>
          )}
        </Flex>
        <Flex alignItems='center' gap={1}>
          {siteBoxDocument &&
            renderActions?.(siteBoxDocument, {
              editQueries: () => sidebarRef.current?.setView('FORM'),
            })}
          {onClose && (
            <IconButton aria-label='close-modal' onClick={onClose}>
              <Clear />
            </IconButton>
          )}
        </Flex>
      </Flex>

      <Flex flex={1} minHeight={0} p={1} gap={1}>
        <Box flex={3} overflow='hidden' position='relative'>
          <SiteBoxDocumentPreview
            siteBoxDocument={previewSiteBoxDocument}
            documentUrl={documentUrl}
            name={currentItem?.name}
            pspdfkitRef={pspdfkitRef}
            readOnly
          />
          {hasMultipleDocuments && (
            <CarouselNavigation
              onPrevious={handlePrevious}
              onNext={handleNext}
              px={3}
            />
          )}
        </Box>
        {siteBoxDocument && previewMode === 'PDF' && (
          <Box flex={1} position='relative'>
            <Box position='absolute' top={0} bottom={0} left={0} right={0}>
              <SiteBoxDocumentSidebar
                key={siteBoxDocument.id}
                ref={sidebarRef}
                organisationId={organisationId}
                projectId={projectId}
                siteBoxDocument={siteBoxDocument}
                pspdfkitRef={pspdfkitRef}
                initialView={initialView}
                readOnly={readOnly}
              />
            </Box>
          </Box>
        )}
      </Flex>
    </Flex>
  )
}
